import React, { useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Button,
  TextField,
  IconButton,
  Paper,
} from '@mui/material';
import OffIcon from '@mui/icons-material/HighlightOff';
import ListIcon from '@mui/icons-material/List';
import ChartIcon from '@mui/icons-material/BarChart';
import GetAppIcon from '@mui/icons-material/GetApp';
import TimeLineList from 'components/TimeLine/TimeLineList';
import TimeLineChart from 'components/TimeLine/TimeLineChart';
import DownloadForm from 'components/DownloadForm';
import useToggler from 'hooks/useToggler';
import { Link } from 'react-router-dom';
import './TimeLine.css';
import config from 'config';

import { randomId } from 'utils/commonTools';
import apiService from 'services/apiService';
import { streamFileToUser } from 'utils/fileTools';
import { translation } from 'utils/translate';
import { useSelector } from 'react-redux';

const LISTVIEW_ENTRIES = {
  /*
  Object for options for the document lists.

  labelField  - the title text
  listFields  - fields displayed in each entry in the TimeLineLists
  sortField   - field used to sort the documents
  */
  lausuntokierros: {
    idField: 'doc_id',
    labelField: 'tunnus',
    listFields: ['title', 'tila', 'asettamis_paiva'],
    sortField: 'asettamis_paiva',
  },
  lausuntokierros_lausunnot: {
    idField: 'id',
    labelField: 'title',
    listFields: ['laatija', 'laatimispaiva'],
    sortField: 'laatimispaiva',
  },
  lausuntokierros_muut: {
    idField: 'id',
    labelField: 'title',
    listFields: ['tyyppi', 'laatija', 'laatimispaiva'],
    sortField: 'laatimispaiva',
  },
  lakialoite: {
    idField: 'doc_id',
    labelField: 'tunnus',
    listFields: ['title', 'asian_tila', 'laadittu'],
    sortField: 'laadittu',
  },
  kansalaisaloite: {
    idField: 'doc_id',
    labelField: 'tunnus',
    listFields: ['title', 'asian_tila', 'laadittu'],
    sortField: 'laadittu',
  },
  hallituksen_esitykset: {
    idField: 'doc_id',
    labelField: 'tunnus',
    listFields: ['title', 'asian_tila', 'laadittu'],
    sortField: 'laadittu',
  },
  he_asiantuntijalausunnot: {
    idField: 'doc_id',
    labelField: 'doc_id',
    listFields: ['title', 'valiokunta', 'date'],
    sortField: 'doc_id',
  },
  valiokunta_asiakirjat: {
    idField: 'doc_id',
    labelField: 'tunnus',
    listFields: ['valiokunta', 'type', 'laadittu'],
    sortField: 'laadittu',
  },
  eduskunnan_vastaukset: {
    idField: 'doc_id',
    labelField: 'tunnus',
    listFields: ['title', 'laadittu'],
    sortField: 'laadittu',
  },
  poytakirja: {
    idField: 'doc_id',
    labelField: 'title',
    listFields: [],
    sortField: 'laadittu',
  },
  treaty: {
    idField: 'doc_id',
    labelField: 'treaty_doc_id',
    listFields: [
      'title',
      'signed_at',
    ],
    sortField: 'treaty_doc_id',
  },
  laws: {
    idField: 'laki_id',
    labelField: 'laki_id',
    listFields: [
      'title',
      'paatos',
      'vahvistettu',
      'voimaantulo',
    ],
    sortField: 'vahvistettu',
  },
};

const sortAlphabetical = (
  a,
  b,
  sortField = null,
  locale = 'fi',
) => {
  const textA = sortField ? a[sortField] : a;
  const textB = sortField ? b[sortField] : b;

  // move null values to the end
  if (!textA) return 1;
  if (!textB) return -1;

  // use 'fi' (by default) to support 'åäö'. works for swedish too
  return textA?.localeCompare(textB, locale);
};

const TimeLine = ({ data, activeDoc, docData }) => {
  const [visualize, setVisualize] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const timelineRef = useRef();

  // download documents state
  const [downloadForm, setDownloadForm] = useState(false);
  const [
    loadingDataFromBackend,
    setLoadingDataFromBackend,
  ] = useState(false);
  const [loadingPercent, setLoadingPercent] = useState(0);
  const [fileDownloadLink, setFileDownloadLink] =
    useState('');
  const [activeDocGroups, setActiveDocGroups] =
    useState(false);

  const lang = useSelector((store) => store.lang)

  // scroll to top of the timeline
  const scrollToTop = () => {
    window.scrollTo({
      top: timelineRef.current.offsetTop - 20,
      behavior: 'smooth',
    });
  };

  const togglers = {
    // hooks/states for opening and closing the document lists
    lausuntokierros: useToggler(),
    lausuntokierros_lausunnot: useToggler(),
    lausuntokierros_muut: useToggler(),
    lakialoite: useToggler(),
    kansalaisaloite: useToggler(),
    hallituksen_esitykset: useToggler(),
    he_asiantuntijalausunnot: useToggler(),
    valiokunta_asiakirjat: useToggler(),
    eduskunnan_vastaukset: useToggler(),
    poytakirja: useToggler(),
    laws: useToggler(),
    treaty: useToggler(),
  };

  const closeAllAndScroll = () => {
    // loop through all togglers and turn them all off
    Object.values(togglers).forEach((obj) => obj.off());
    scrollToTop();
  };

  const sortedData = useMemo(() => {
    // filter out length fields and others not specified in LISTIVEW_ENTRIES
    // NOTE: this does exclude lausuntopalvelu_lausunnot which was not used previously anyhow
    return Object.entries(data)
      .filter((entry) =>
        //LISTVIEW_ENTRIES.hasOwnProperty(entry[0])
        Object.prototype.hasOwnProperty.call(LISTVIEW_ENTRIES, entry[0])
      )
      .reduce((acc, [group, items]) => {
        // sort documents based on its group's sortField
        const { sortField, labelField } = LISTVIEW_ENTRIES[group];
        let sortedItems = [];
        // in case of doc_id, sort alphabetically, otherwise by date
        if (sortField === 'doc_id') {
          sortedItems = items.sort((a, b) =>
            sortAlphabetical(a, b, sortField),
          );
        } else {
          sortedItems = items.sort((a, b) => {
            // sort primarily by date, secondarily by title alphabetically
            const sortResult = new Date(a[sortField]) - new Date(b[sortField]);
            if (sortResult !== 0) return sortResult;
            return sortAlphabetical(a, b, labelField);
          });
        }
        return { ...acc, [group]: sortedItems };
      }, {});
  }, [data]);

  const activeData = useMemo(() => {
    if (!searchTerm) {
      return sortedData;
    }
    // filter data with searchTerm
    return (
      Object.entries(sortedData)
        .reduce(
          (acc, [docGroup, items]) => ({
            ...acc,
            [docGroup]: items.filter((item) =>
              Object.values(item).some(
                (value) =>
                  value &&
                  String(value)
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase()),
              ),
            ),
          }),
          {},
        )
    );
  }, [sortedData, searchTerm]);

  const handleDownload = async ({
    metaFormat,
    fileFormats,
  }) => {
      const searchTerms = Object.entries(data)
      .map((searchObject) =>
        config.activeIndices.includes(searchObject[0])
          ? searchObject[1]
              .map((obj) => obj.doc_id)
              .join(',')
          : false,
      )
      .filter((obj) => obj)
      .join(',');

    // show spinner
    setLoadingDataFromBackend(true);

    let jobId = '';
    if (fileFormats) {
      jobId = randomId(8);
    }

    try {
      const result = await apiService.postData(
        'download/all',
        {
          term: searchTerms,
          project: true,
          format: metaFormat,
          content: fileFormats,
          job_id: jobId,
          active_doc_groups: activeDocGroups.join(),
          lang: lang
        },
        'text'
      );

      if (result) {
        streamFileToUser({
          data: result,
          format: metaFormat,
          fileName: `project_exported_${new Date()
            .toISOString()
            .slice(0, 10)}`,
        });
      }

      if (fileFormats) {
        const interval = setInterval(async () => {
          const { status } =
            await apiService.checkFileStatus(jobId);

          if (status === 'DONE') {
            clearInterval(interval);
            setFileDownloadLink(jobId);
            setLoadingDataFromBackend(false);
            // eslint-disable-next-line no-restricted-globals
          } else if (!isNaN(status)) {
            setLoadingPercent(parseFloat(status));
          } else {
            clearInterval(interval);
            setLoadingDataFromBackend(false);
            setDownloadForm(false);
          }
        }, 3000);
      } else {
        setLoadingDataFromBackend(false);
        setDownloadForm(false);
      }
    } catch (error) {
      setLoadingDataFromBackend(false);
      setDownloadForm(false);
      // eslint-disable-next-line
      alert(error);
    }
  };

  const handleDownloadZippedFile = () => {
    setLoadingDataFromBackend(false);
  };

  const setActiveDocGroupsFromTimeline = (docGroups) => {
    if (
      Object.keys(config.docGroups).length !==
      docGroups.length
    ) {
      setActiveDocGroups(docGroups);
    }
  };

  return (
    <Paper className="timeline-wrapper">
      {data.hallituksen_esitykset &&
        data.hallituksen_esitykset[0] &&
        data.hallituksen_esitykset[0].title && (
          <div className="hanke">
            <div className="identifier">
            {lang === "se" ?
                <Link
                  to={`/asiakirjat/hallituksen-esitykset/${data.hallituksen_esitykset[0].doc_id}?lang=se`}>
                  {translation("timeline", "project", "se")}
                  {' '}
                  {data.hallituksen_esitykset[0].tunnus_sv}
                </Link>
                :
                <Link
                  to={`/asiakirjat/hallituksen-esitykset/${data.hallituksen_esitykset[0].doc_id}`}>
                  {translation("timeline", "project", "fi")}
                  {' '}
                  {data.hallituksen_esitykset[0].tunnus}
                </Link>
            }
            </div>
            <h3 className="hanke__title">
              {lang === "se" ?
                <>{data.hallituksen_esitykset[0].title_sv || data.hallituksen_esitykset[0].title}</>
                :
                <>{data.hallituksen_esitykset[0].title || data.hallituksen_esitykset[0].title_sv}</>
              }
            </h3>
          </div>
        )}
      {data.hallituksen_esitykset && data.hallituksen_esitykset.length === 0 &&
        <div className="hanke">
          <span className='asiakirja'>{translation('asiakirja', lang)}: </span><span className='title_notHE'>{docData?._source?.tunnus} - {docData?._source?.title}</span>
        </div>
      }
      <div className="timeline-header" ref={timelineRef}>
        <h4 className="timeline-header__title">
          {translation("timeline", "filter_documents", lang)}
        </h4>
        <TextField
          id="search-timeline"
          label={translation("timeline", "filter", lang)}
          className="timeline-header__search"
          name="timeline-term"
          value={searchTerm}
          onChange={(event) =>
            setSearchTerm(event.target.value)
          }
        />
        {!!searchTerm && (
          <IconButton
            aria-label="poista rajaus"
            component="span"
            onClick={() => setSearchTerm('')}
          >
            <OffIcon />
          </IconButton>
        )}
        {/* result tools */}
        <div className="timeline-header__button-area">
          {downloadForm ? (
            <Button
              className="button--right"
              disableTouchRipple
              onClick={() => setDownloadForm(!downloadForm)}
            >
              {visualize ? (
                <>
                  <ListIcon className="button-icon" />
                  {translation("timeline", "show_timeline", lang)}
                </>
              ) : (
                <>
                  <ChartIcon className="button-icon" />
                  {translation("timeline", "show_list", lang)}
                </>
              )}
            </Button>
          ) : (
            <>
              <Button
                className="button--right"
                disableTouchRipple
                onClick={() =>
                  setDownloadForm(!downloadForm)
                }
              >
                <>
                  <GetAppIcon className="button-icon" />
                  {translation("timeline", "download", lang)}
                </>
              </Button>
              <Button
                className="button--right"
                disableTouchRipple
                onClick={() => setVisualize(!visualize)}
              >
                {visualize ? (
                  <>
                    <ListIcon className="button-icon" />
                  {translation("timeline", "show_list", lang)}
                  </>
                ) : (
                  <>
                    <ChartIcon className="button-icon" />
                  {translation("timeline", "show_timeline", lang)}
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </div>

      {downloadForm ? (
        <div className="download-form-wrapper download-form-wrapper--open">
          <DownloadForm
            loadingDataFromBackend={loadingDataFromBackend}
            loadingPercent={loadingPercent}
            fileDownloadLink={fileDownloadLink}
            handleDownload={handleDownload}
            handleDownloadZippedFile={
              handleDownloadZippedFile
            }
          />
        </div>
      ) : visualize ? (
        // Time line view
        <TimeLineChart
          data={activeData}
          scroll={scrollToTop}
          activeDoc={activeDoc}
          setActiveDocGroups={
            setActiveDocGroupsFromTimeline
          }
        />
      ) : (
        // List view
        <Grid
          container
          spacing={4}
          className="timeline-listview"
        >
          {Object.entries(LISTVIEW_ENTRIES).map(
            ([key, options]) =>
              Object.prototype.hasOwnProperty.call(activeData, key) && (
                <Grid item xs={12} sm={6} lg={4} key={key}>
                  <TimeLineList
                    isOpen={togglers[key].state}
                    toggle={togglers[key].toggle}
                    onItemClick={closeAllAndScroll}
                    data={activeData[key]}
                    docType={key}
                    idField={options.idField}
                    labelField={options.labelField}
                    listFields={options.listFields}
                  />
                </Grid>
              ),
          )}
        </Grid>
      )}
    </Paper>
  );
};

TimeLine.propTypes = {
  data: PropTypes.shape({
    hallituksen_esitykset: PropTypes.arrayOf(
      PropTypes.shape({
        tunnus: PropTypes.string,
        title: PropTypes.string,
      }),
    ),
  }).isRequired,
  activeDoc: PropTypes.string.isRequired,
  docData: PropTypes.shape({}),
};
TimeLine.defaultProps = {
  docData: {}
}

export default TimeLine;
